import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Sparkles, Loader2 } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { useDailyMicronutrients, NutrientData } from '@/hooks/useMicronutrients';

interface MicronutrientsCardProps {
  date?: Date;
}

/**
 * Карточка-сводка микронутриентов для Dashboard.
 * Мотивирующий стиль — показывает что хорошо, что можно добрать.
 */
export function MicronutrientsCard({ date }: MicronutrientsCardProps) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { nutrients, stats, loading } = useDailyMicronutrients(date);

  function handleOpenDetails() {
    haptic('light');
    nav('/micronutrients');
  }

  // ============================================
  // СОСТОЯНИЕ: ЗАГРУЗКА
  // ============================================
  if (loading) {
    return (
      <div className="card-elevated p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-emerald-glow" />
            <span className="label">Микронутриенты</span>
          </div>
        </div>
        <div className="flex items-center gap-2 text-ink-dim text-xs">
          <Loader2 size={12} className="animate-spin" />
          <span>Анализирую...</span>
        </div>
      </div>
    );
  }

  // ============================================
  // СОСТОЯНИЕ: НЕТ ДАННЫХ
  // ============================================
  if (!nutrients.length) {
    return (
      <div className="card-elevated p-4 opacity-70">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-emerald-glow" />
          <span className="label">Микронутриенты</span>
        </div>
        <p className="text-xs text-ink-dim leading-relaxed">
          Запиши хоть один приём пищи — покажу что есть, а чего стоит добрать.
        </p>
      </div>
    );
  }

  // Считаем нутриенты с РЕАЛЬНЫМИ данными
  // (если у юзера 0 — это не значит "не хватает", могло просто не быть таких продуктов)
  const hasAnyData = nutrients.some((n) => n.consumed > 0);

  if (!hasAnyData) {
    return (
      <div className="card-elevated p-4 opacity-70">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-emerald-glow" />
          <span className="label">Микронутриенты</span>
        </div>
        <p className="text-xs text-ink-dim leading-relaxed">
          Записи пока без анализа состава. Сфоткай еду или опиши Дофи — посчитаю всё.
        </p>
      </div>
    );
  }

  // ============================================
  // ВЫБОР ХАЙЛАЙТОВ ДЛЯ ПОКАЗА
  // ============================================

  // Хорошо (top-3 по убыванию % от нормы)
  const goodHighlights = stats.good
    .filter((n) => n.consumed > 0)
    .sort((a, b) => b.percentage - a.percentage)
    .slice(0, 3);

  // Не хватает (top-3 по возрастанию % — самое проблемное первое)
  const lackHighlights = nutrients
    .filter((n) => n.status === 'lack' && n.target_type !== 'reverse')
    .sort((a, b) => a.percentage - b.percentage)
    .slice(0, 3);

  // Близко к норме
  const warningHighlights = stats.warning.slice(0, 2);

  // ============================================
  // СОСТОЯНИЕ: ВСЁ В НОРМЕ
  // ============================================
  if (stats.lack.length === 0 && stats.warning.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card-elevated p-4 border-emerald-glow/30"
      >
        <button
          type="button"
          onClick={handleOpenDetails}
          className="w-full text-left"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-emerald-glow" />
              <span className="label">Микронутриенты</span>
            </div>
            <ChevronRight size={14} className="text-ink-dim" />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-2xl">🌟</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-emerald-glow">
                Отличный день!
              </div>
              <div className="text-[11px] text-ink-dim">
                Все {stats.total} нутриентов в норме
              </div>
            </div>
          </div>
        </button>
      </motion.div>
    );
  }

  // ============================================
  // ОСНОВНОЕ СОСТОЯНИЕ: СВОДКА
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="card-elevated p-4"
    >
      <button
        type="button"
        onClick={handleOpenDetails}
        className="w-full text-left"
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-emerald-glow" />
            <span className="label">Микронутриенты</span>
          </div>
          <ChevronRight size={14} className="text-ink-dim" />
        </div>

        {/* Контент */}
        <div className="space-y-2.5">
          {/* Хорошо */}
          {goodHighlights.length > 0 && (
            <HighlightRow
              icon="🟢"
              title="Хорошо с:"
              items={goodHighlights}
              color="text-emerald-glow"
            />
          )}

          {/* Не хватает */}
          {lackHighlights.length > 0 && (
            <HighlightRow
              icon="🔴"
              title="Не хватает:"
              items={lackHighlights}
              color="text-danger"
            />
          )}

          {/* Близко к норме (показываем только если нет lack) */}
          {lackHighlights.length === 0 && warningHighlights.length > 0 && (
            <HighlightRow
              icon="🟡"
              title="Можно добрать:"
              items={warningHighlights}
              color="text-warn"
            />
          )}
        </div>

        {/* Подсказка */}
        <div className="mt-3 pt-2 border-t border-line/40 flex items-center justify-between">
          <span className="text-[10px] text-ink-dim">
            {stats.good.length}/{stats.total} в норме
          </span>
          <span className="text-[10px] text-emerald-glow flex items-center gap-0.5 font-medium">
            Подробнее
            <ChevronRight size={10} />
          </span>
        </div>
      </button>
    </motion.div>
  );
}

// ============================================
// СУБ-КОМПОНЕНТ: СТРОКА С ХАЙЛАЙТАМИ
// ============================================

function HighlightRow({
  icon,
  title,
  items,
  color,
}: {
  icon: string;
  title: string;
  items: NutrientData[];
  color: string;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <span className={`text-xs font-medium ${color}`}>{title}</span>{' '}
        <span className="text-xs text-ink">
          {items.map((n, i) => (
            <span key={n.key}>
              {n.label.toLowerCase()}
              {i < items.length - 1 && ', '}
            </span>
          ))}
        </span>
      </div>
    </div>
  );
}
